(function () {
  const MODAL_ID = "sm-klaviyo-bis-modal";
  const BIS_ENDPOINT = "https://a.klaviyo.com/client/back-in-stock-subscriptions/";
  const API_REVISION = "2026-04-15";

  let modalElement = null;
  let activeVariantId = null;
  let activeProductId = null;
  let activeProductTitle = "";
  let klaviyoFormOpened = false;

  document.addEventListener("klaviyoForms", function (event) {
    const eventType = event?.detail?.type;
    if (["open", "stepSubmit", "submit"].includes(eventType)) {
      klaviyoFormOpened = true;
    }
  });

  function getKlaviyoCompanyId() {
    if (window.klaviyoBisConfig?.companyId) {
      return window.klaviyoBisConfig.companyId;
    }

    const scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (const script of scripts) {
      const match = script.src.match(/company_id=([^&]+)/);
      if (match) return decodeURIComponent(match[1]);
    }

    if (window.klaviyo?.account) return window.klaviyo.account;
    if (window.klaviyoModulesObject?.companyId) return window.klaviyoModulesObject.companyId;

    return null;
  }

  function refreshKlaviyoBis() {
    if (window._klaviyo && typeof window._klaviyo.init === "function") {
      try {
        window._klaviyo.init();
      } catch (error) {
        console.warn("Klaviyo BIS reinit failed", error);
      }
    }
  }

  function isKlaviyoModalOpen() {
    return klaviyoFormOpened || document.body.classList.contains("klaviyo-prevent-body-scrolling");
  }

  function ensureModal() {
    if (modalElement) return modalElement;

    modalElement = document.createElement("div");
    modalElement.id = MODAL_ID;
    modalElement.className = "sm-klaviyo-bis-modal";
    modalElement.innerHTML = `
      <div class="sm-klaviyo-bis-modal__overlay" data-klaviyo-bis-close></div>
      <div class="sm-klaviyo-bis-modal__panel" role="dialog" aria-modal="true" aria-labelledby="${MODAL_ID}-title">
        <button type="button" class="sm-klaviyo-bis-modal__close" data-klaviyo-bis-close aria-label="Close">&times;</button>
        <h2 class="sm-klaviyo-bis-modal__title" id="${MODAL_ID}-title"></h2>
        <p class="sm-klaviyo-bis-modal__body">Register to receive a notification when this item comes back in stock.</p>
        <form class="sm-klaviyo-bis-modal__form">
          <label class="sr-only" for="${MODAL_ID}-email">Email</label>
          <input
            id="${MODAL_ID}-email"
            class="sm-klaviyo-bis-modal__input"
            type="email"
            name="email"
            required
            autocomplete="email"
            placeholder="Email"
          >
          <button type="submit" class="sm-klaviyo-bis-modal__submit">Notify me when back in stock</button>
        </form>
        <p class="sm-klaviyo-bis-modal__message" hidden></p>
      </div>
    `;
    document.body.appendChild(modalElement);

    modalElement.querySelectorAll("[data-klaviyo-bis-close]").forEach((node) => {
      node.addEventListener("click", closeModal);
    });

    modalElement.querySelector("form").addEventListener("submit", onFallbackSubmit);
    return modalElement;
  }

  function openFallbackModal(variantId, productId, productTitle) {
    activeVariantId = variantId;
    activeProductId = productId;
    activeProductTitle = productTitle || document.title;
    const modal = ensureModal();
    const title = modal.querySelector(".sm-klaviyo-bis-modal__title");
    const message = modal.querySelector(".sm-klaviyo-bis-modal__message");
    const emailInput = modal.querySelector('input[type="email"]');
    const form = modal.querySelector("form");
    title.textContent = activeProductTitle;
    message.hidden = true;
    message.className = "sm-klaviyo-bis-modal__message";
    emailInput.value = "";
    form.hidden = false;
    modal.classList.add("is-open");
    emailInput.focus();
  }

  function closeModal() {
    if (!modalElement) return;
    modalElement.classList.remove("is-open");
  }

  function postRequest(url, headers, body) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) return;
        if (xhr.status === 0) {
          reject(new TypeError("Network request failed"));
          return;
        }
        resolve({
          status: xhr.status,
          ok: xhr.status >= 200 && xhr.status < 300,
          responseText: xhr.responseText,
          getResponseHeader: (name) => xhr.getResponseHeader(name)
        });
      };
      xhr.onerror = function () {
        reject(new TypeError("Network request failed"));
      };
      xhr.send(body);
    });
  }

  function parseErrorResponse(response) {
    const contentType = response.getResponseHeader("content-type") || "";
    const text = response.responseText || "";
    try {
      if (contentType.includes("json")) {
        const data = JSON.parse(text);
        if (data?.errors?.length) {
          return data.errors.map((error) => error.detail || error.title).join(" ");
        }
        if (data?.message) return data.message;
        return JSON.stringify(data);
      }
      return text;
    } catch (error) {
      return `Request failed: ${response.status}`;
    }
  }

  async function subscribeViaModernApi(companyId, email, variantId) {
    const response = await postRequest(
      `${BIS_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
      {
        Accept: "application/vnd.api+json",
        "Content-Type": "application/vnd.api+json",
        revision: API_REVISION
      },
      JSON.stringify({
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            channels: ["EMAIL"],
            profile: {
              data: {
                type: "profile",
                attributes: { email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      })
    );

    if (response.status === 202 || response.ok) {
      return true;
    }

    const errorMessage = parseErrorResponse(response);
    throw new Error(errorMessage || `Modern request failed: ${response.status}`);
  }

  async function subscribeToBackInStock(companyId, email, variantId) {
    await subscribeViaModernApi(companyId, email, variantId);
  }

  function showModalMessage(messageEl, type, text) {
    if (!messageEl) return;
    messageEl.hidden = false;
    messageEl.className = `sm-klaviyo-bis-modal__message${type ? ` is-${type}` : ""}`;
    messageEl.textContent = text;
  }

  async function onFallbackSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const companyId = getKlaviyoCompanyId();
    const emailInput = form.querySelector('input[type="email"]');
    const message = modalElement && modalElement.querySelector(".sm-klaviyo-bis-modal__message");
    const email = emailInput ? emailInput.value.trim() : "";

    if (!companyId) {
      showModalMessage(message, "error", "Unable to connect to notification service. Please try again later.");
      return;
    }

    const variantId = activeVariantId || getVariantIdFromPage();
    if (!variantId) {
      showModalMessage(message, "error", "Please select a size and try again.");
      return;
    }

    showModalMessage(message, "", "Submitting...");

    try {
      await subscribeToBackInStock(companyId, email, variantId);
      showModalMessage(message, "success", "You're in! We'll let you know when it's back.");
      form.hidden = true;
    } catch (error) {
      console.error("Klaviyo BIS fallback failed", error);
      const isNetworkError = error instanceof TypeError;
      showModalMessage(
        message,
        "error",
        isNetworkError
          ? "Network error. Please check your connection or disable ad blockers, then try again."
          : "Something went wrong. Please try again."
      );
      form.hidden = false;
    }
  }

  function getVariantIdFromPage() {
    const urlVariant = new URLSearchParams(window.location.search).get("variant");
    if (urlVariant) return urlVariant;

    const variantInput = document.querySelector('form[id^="product-form-"] input[name="id"]');
    if (variantInput?.value) return variantInput.value;

    const bisContainer = document.querySelector("[data-klaviyo-bis]:not(.hidden)");
    return bisContainer?.dataset?.variantId || null;
  }

  function getVariantIdFromContainer(container) {
    return getVariantIdFromPage() || container?.dataset?.variantId || null;
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest(".klaviyo-bis-trigger");
    if (!trigger) return;

    if (trigger.tagName === "A") {
      event.preventDefault();
    }

    const container = trigger.closest("[data-klaviyo-bis]");
    const variantId = getVariantIdFromContainer(container);
    const productId = container?.dataset?.productId || null;
    const productTitle =
      document.querySelector(".sm-product-info__title, .sm-mobile-atc-modal__product-title, h1")?.textContent?.trim() ||
      document.title;

    refreshKlaviyoBis();
    klaviyoFormOpened = false;

    window.setTimeout(function () {
      if (!isKlaviyoModalOpen()) {
        openFallbackModal(variantId, productId, productTitle);
      }
    }, 400);
  }

  function syncVariantIds(variantId) {
    if (!variantId) return;
    document.querySelectorAll("[data-klaviyo-bis]").forEach((container) => {
      container.dataset.variantId = String(variantId);
    });
  }

  document.addEventListener("click", handleTriggerClick);

  document.addEventListener("DOMContentLoaded", function () {
    refreshKlaviyoBis();
  });

  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
    window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
      const variant = event?.data?.variant;
      if (variant?.id) syncVariantIds(variant.id);
      refreshKlaviyoBis();
    });
  }

  document.addEventListener("variant-change", function () {
    window.setTimeout(refreshKlaviyoBis, 100);
  });

  window.refreshKlaviyoBis = refreshKlaviyoBis;
})();
